import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const when = (d) => (d ? new Date(d).toLocaleString() : "—");
const SPANS = [[1, "last 1 hour"], [24, "last 24 hours"], [168, "last 7 days"]];

const Row = ({ g, picked, onPick, act }) => {
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const flagged = g.suspicious || g.held;
  const a = g.activity || {};
  const dls = (a.originals_dl || 0) + (a.zips_dl || 0);
  return (
    <div className="admin-row flex-wrap" data-testid={`self-guest-${g.id}`}>
      <input type="checkbox" className="w-4 h-4 accent-neutral-900" checked={picked} onChange={onPick} data-testid={`self-guest-select-${g.id}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-neutral-900 flex flex-wrap items-center gap-1.5">
          {g.username}
          <span className="role-chip role-guest">guest · {g.days_left} day{g.days_left === 1 ? "" : "s"} left</span>
          {g.held && <span className="role-chip role-pending" data-testid={`self-guest-held-${g.id}`}>waiting for approval</span>}
          {flagged && (
            <button className="role-chip role-blocked inline-flex items-center gap-1" onClick={() => setOpen(!open)} data-testid={`self-guest-suspicious-${g.id}`}>
              <AlertTriangle size={11} /> Suspicious <ChevronDown size={11} className={open ? "rotate-180" : ""} />
            </button>
          )}
        </div>
        <div className="text-xs text-neutral-500">{g.email || "no email"} · signed up {when(g.created_at)} · last visit {when(g.last_visit)}</div>
        <button className="text-xs text-neutral-700 underline mt-0.5" onClick={() => setMore(!more)} data-testid={`self-guest-activity-toggle-${g.id}`}>
          {(a.recent_albums || []).length} album{(a.recent_albums || []).length === 1 ? "" : "s"} · {dls} download{dls === 1 ? "" : "s"}
        </button>
        {more && (
          <div className="text-xs text-neutral-700 mt-1 grid gap-0.5" data-testid={`self-guest-activity-${g.id}`}>
            <span>{a.visits || 0} visits · {a.photos_viewed || 0} photos viewed · {a.originals_dl || 0} originals · {a.zips_dl || 0} ZIPs · {a.favourites || 0} favourites</span>
            {(a.recent_albums || []).map((r) => <span key={r.id}>· {r.name} — {when(r.at)}</span>)}
          </div>
        )}
        {open && (
          <ul className="text-xs text-red-800 mt-1 list-disc pl-4" data-testid={`self-guest-reasons-${g.id}`}>
            {g.risk_reasons.map((r) => <li key={r.code}>{r.text}</li>)}
          </ul>
        )}
      </div>
      {flagged && <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => act(g, "ok")} data-testid={`self-guest-ok-${g.id}`}>{g.held ? "Approve" : "Mark as OK"}</button>}
      <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => act(g, "block")} data-testid={`self-guest-block-${g.id}`}>Block</button>
      <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => act(g, "delete")} data-testid={`self-guest-delete-${g.id}`}>Delete</button>
    </div>
  );
};

// Self-signup guests (deleted 7 days after creation)
export const SelfGuests = ({ needle = "", onlySuspicious = false, onCount }) => {
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [span, setSpan] = useState(24);
  const load = useCallback(() => api.get("/admin/guest-signups").then((r) => { setRows(r.data.guests); onCount?.(r.data.suspicious); setPicked(new Set()); }), [onCount]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const act = (g, kind) => {
    if (kind === "ok") return run(() => api.post(`/admin/guest-signups/${g.id}/ok`), g.held ? "Approved" : "Marked as OK");
    const ask = kind === "block" ? `Block ${g.username}? Their username, email and device can't sign up again.` : `Delete guest ${g.username}?`;
    if (window.confirm(ask)) run(() => (kind === "block" ? api.post(`/admin/guest-signups/${g.id}/block`) : api.delete(`/admin/guest-signups/${g.id}`)), kind === "block" ? "Blocked" : "Deleted");
    return null;
  };
  const bulk = async (body, label) => {
    try {
      const { data } = await api.post("/admin/guest-signups/bulk-delete", { ...body, dry_run: true });
      if (!data.count) return toast.info("No guests to delete");
      if (!window.confirm(`Delete ${data.count} guest${data.count === 1 ? "" : "s"} ${label}?`)) return null;
      return run(() => api.post("/admin/guest-signups/bulk-delete", body), `Deleted ${data.count}`);
    } catch (e) { return toast.error(errMsg(e)); }
  };
  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const shown = (rows || []).filter((g) => (!needle || `${g.username} ${g.email || ""}`.toLowerCase().includes(needle)) && (!onlySuspicious || g.suspicious || g.held));
  return (
    <section className="mt-6" data-testid="self-guests">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold text-neutral-900 mr-auto">{onlySuspicious ? "Suspicious guests" : "Self-signup guests"} ({shown.length})</h3>
        <span className="text-xs text-neutral-600">Delete guests created in the</span>
        <select className="text-xs border rounded px-2 py-1 bg-white" value={span} onChange={(e) => setSpan(Number(e.target.value))} data-testid="self-guests-span">
          {SPANS.map(([h, l]) => <option key={h} value={h}>{l}</option>)}
        </select>
        <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => bulk({ since_hours: span }, `created in the ${SPANS.find((s) => s[0] === span)[1]}`)} data-testid="self-guests-bulk-delete">Delete…</button>
        {picked.size > 0 && <button className="btn-outline !py-1 !px-3 text-xs !text-red-700" onClick={() => bulk({ ids: [...picked] }, "selected")} data-testid="self-guests-delete-selected">Delete selected ({picked.size})</button>}
      </div>
      {!shown.length && <p className="text-sm text-neutral-500" data-testid="self-guests-empty">{onlySuspicious ? "Nothing suspicious right now." : "No self-signup guests."}</p>}
      <div className="admin-table">
        {shown.map((g) => <Row key={g.id} g={g} picked={picked.has(g.id)} onPick={() => toggle(g.id)} act={act} />)}
      </div>
    </section>
  );
};
